import * as Phaser from 'phaser';
import { ratConfig } from '../../../common/data/rat-config';
import { RatPosition } from '../../../common/enums/rat-position';
import { Events, EventsOption } from '../events';
import { EventKey } from '../../../common/enums/event-key';
import { ElevatorView } from '../viewes/elevator-view';
import { imageSize, tileSize } from 'src/app/common/data/config';
import { ImageKey } from '../../../common/enums/image-key';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Point } from '../../../common/types/size';
import JSZip from 'jszip';
import { Position, Tile, Tiles } from '../../../common/types/tiles';
import { ComplexWallItem, WallItem } from '../../../common/types/wall-item';
import { environment } from 'src/environments/environment';
import { TileImage } from '../viewes/tile-image';

interface TileData {
  tilesData: WallItem;
  tileSprite: Phaser.GameObjects.Image;
}

export class MainScreen extends Phaser.GameObjects.Container {
  private floor!: Phaser.GameObjects.Image;
  private previewImage!: Phaser.GameObjects.Image | null;
  private dragStarted!: boolean;
  private ratSprite!: Phaser.GameObjects.Sprite;
  private ratMask!: Phaser.Display.Masks.GeometryMask;
  private currentClickedTile: Phaser.GameObjects.Image | undefined;
  private ratScale: number = 0.6;
  private floorPositions = {
    first: imageSize.h - 35 * this.ratScale,
    second: 950 - 35 * this.ratScale,
  };
  private elevatorView!: ElevatorView;
  private tilePointerDown!: boolean;
  private timeoutId: unknown;
  private activeTile: Phaser.GameObjects.Image | undefined;
  private unsubscribe$ = new Subject<void>();
  private defaultTileSize: Point = { x: 1, y: 1 };
  private tilesData!: Tiles;
  private tileViews!: any[];
  private maximums?: Position;
  private activeTilesData!: any[];
  private currentClickedImage: Phaser.GameObjects.Image | undefined;
  private cartItems: ComplexWallItem[] | undefined;
  private editedTileClicked: boolean = false;
  private staticTiles!: Position[];

  constructor(scene: Phaser.Scene) {
    super(scene);
    this.init();
  }

  destroy(fromScene?: boolean) {
    super.destroy(fromScene);
    if (this.timeoutId) {
      clearTimeout(this.timeoutId as number);
    }
  }

  private async init(): Promise<void> {
    await this.loadTilesData();
    this.calculateTilesBounds();
    this.initFloor();
    this.initElevatorView();
    this.createRatSpriteAndPlayAnimation();
    this.initTiles();
    this.subscribeToEvents();
    Events.emit({
      key: EventKey.WallLoaded,
    });
  }

  private createRatSpriteAndPlayAnimation(): void {
    this.ratSprite = this.scene.add.sprite(0, 0, 'rats');
    this.ratSprite.setScale(this.ratScale);
    this.ratMask = this.scene.make
      .graphics({})
      .fillRect(0, 0, imageSize.w, imageSize.h)
      .createGeometryMask();
    this.scene.anims.create({
      key: 'rat-walk',
      frames: 'rats',
      frameRate: 9,
      repeat: -1,
    });
    this.playRatAnimation();
  }

  private playRatAnimation() {
    const animation = ratConfig[Math.floor(Math.random() * 4)];
    const y =
      animation.floor === RatPosition.FirstFloor
        ? this.floorPositions.first
        : this.floorPositions.second;
    let startX;
    let endX;
    let scaleDirection;
    if (animation.moveDirection === RatPosition.Left) {
      startX = -this.ratSprite.width;
      endX = imageSize.w + this.ratSprite.width;
      scaleDirection = -1;
    } else {
      startX = imageSize.w + this.ratSprite.width;
      endX = -this.ratSprite.width;
      scaleDirection = 1;
    }
    this.ratSprite.setPosition(startX, y);
    this.ratSprite.setScale(scaleDirection * this.ratScale, this.ratScale);
    this.ratSprite.play('rat-walk');
    this.scene.tweens.add({
      targets: this.ratSprite,
      x: endX,
      y,
      duration: 4000,
      onComplete: () => this.toggleRatAnimation(),
    });
    this.ratSprite.setMask(this.ratMask);
    this.bringToTop(this.ratSprite);
  }

  private toggleRatAnimation() {
    this.ratSprite.stop();
    this.timeoutId = setTimeout(
      () => this.playRatAnimation(),
      Math.floor(Math.random() * 10) * 1000
    );
  }

  private initFloor(): void {
    this.floor = this.scene.add.image(0, 0, 'floor');
    this.add(this.floor);
    this.scene.add.image(0, imageSize.h, 'ground').setOrigin(0, 0);
    this.floor.setInteractive();
    this.floor.setOrigin(0, 0);
    this.scene.input.setDraggable(this.floor);
    this.scene.input.dragDistanceThreshold = 15;
    this.scene.input.topOnly = false;
    this.scene.input.on('gameout', () => this.activeTile?.setAlpha(0.0001));
    this.floor.on('dragstart', () => {
      this.dragStarted = true;
      this.scene.game.canvas.style.cursor = 'grabbing';
      this.tilePointerDown = false;
    });
    this.floor.on('dragend', () => {
      this.scene.game.canvas.style.cursor = 'pointer';
      this.dragStarted = false;
    });
    this.floor.on('drag', (pointer: any, dragX: number, dragY: number) => {
      this.scene.cameras.main.scrollX += -dragX;
      this.scene.cameras.main.scrollY += -dragY;
    });
    this.floor.on('wheel', (pointer: any, dragX: number, dragY: number) => {
      this.scene.cameras.main.scrollX += dragX;
      this.scene.cameras.main.scrollY += dragY;
    });
  }

  private initTiles(): void {
    this.tileViews = [];
    this.staticTiles = this.scene.cache.json.get('staticTiles');
    this.tilesData.forEach(
      (tile: Tile) =>
        (tile.unavailable = this.staticTiles.some(
          (position: Position) =>
            position.x === tile.position.x && position.y === tile.position.y
        ))
    );

    for (let i = 0; i < this.tilesData.length; i++) {
      this.tileViews.push({
        tileSprite: this.initTile(this.tilesData[i]),
        tileData: this.tilesData[i],
      });
    }
    this.tileViews.sort(
      (a, b) => Number(a.tileData.id) - Number(b.tileData.id)
    );
  }

  private initTile(tile: Tile): Phaser.GameObjects.Image {
    const image = this.scene.add.image(
      tile.position.x * tileSize,
      tile.position.y * tileSize,
      'tile'
    );
    image.setOrigin(0, 0);
    image.setAlpha(0.0001);
    image.setInteractive();
    image.on('pointerover', () => {
      if (!this.dragStarted) {
        const tint = tile.unavailable ? 0xa00101 : 0x75ff69;
        this.activeTile = image.setAlpha(0.7).setTint(tint);
      }
    });
    image.on('pointerdown', () => (this.tilePointerDown = true));
    image.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (
        this.tilePointerDown &&
        !this.editedTileClicked &&
        !tile.unavailable
      ) {
        const clickedLeftSide = pointer.x <= this.scene.game.scale.width / 2;
        this.tilePointerDown = false;
        this.currentClickedTile = image;
        Events.emit({
          key: EventKey.TileClick,
          data: {
            clickedLeftSide,
            tilesSize: this.getAvailableTilesSize(image),
          },
        });
      }
    });
    image.on('pointerout', () => image.setAlpha(0.0001));
    return image;
  }

  private getAvailableTilesSize(tileImage: Phaser.GameObjects.Image): any {
    const clickedTileViewData = this.tileViews.find(
      (tv) => tv.tileSprite === tileImage
    );
    let maxStep = 4;
    const tx = clickedTileViewData.tileData.position.x;
    const ty = clickedTileViewData.tileData.position.y;
    const cartAndWallTilesId: string[] = (
      this.cartItems as ComplexWallItem[]
    )?.reduce(
      (ids: string[], ca: ComplexWallItem) => ids.concat(ca.tiles as any),
      []
    );
    const tileAndWalletPositions = this.tilesData
      .filter((td) => cartAndWallTilesId.some((cwt) => cwt === td.id))
      .map((td) => td.position);
    const unavailableTiles = [...this.staticTiles, ...tileAndWalletPositions];
    main: for (let i = 1; i <= 4; i++) {
      maxStep = i;
      for (let j = 0; j <= i; j++) {
        if (
          unavailableTiles.some(
            (st) =>
              (st.x === tx + j && st.y === ty + i) ||
              (st.x === tx + i && st.y === ty + j)
          ) ||
          tx + j > (this.maximums?.x || 0) ||
          ty + j > (this.maximums?.y || 0) ||
          tx + i > (this.maximums?.x || 0) ||
          ty + i > (this.maximums?.y || 0)
        ) {
          break main;
        }
      }
    }
    return maxStep;
  }

  private initializeCartData(event: EventsOption<unknown>): void {
    this.changeWallDataLoading(true);
    const prevCartItems = this.cartItems;
    this.cartItems = (event.data as any).cartItems as ComplexWallItem[];
    if (!this.cartItems?.length) {
      this.changeWallDataLoading(false);
      return;
    }
    const tilesData = this.getTilesData(prevCartItems);
    if (this.scene.load.list.size === 0) {
      this.changeWallDataLoading(false);
      return;
    }
    this.scene.load.start();
    const textureKeys = this.scene.textures.getTextureKeys();
    this.scene.load.once(Phaser.Loader.Events.COMPLETE, () => {
      this.cartItems?.forEach(
        (
          cartItem: ComplexWallItem,
          index: number,
          cartItems: ComplexWallItem[]
        ) => {
          if (cartItems.length - 1 === index) {
            this.destroyPreviewImage();
          }
          const hasSoledTile = cartItem.tiles.some(
            (tile) => tile.userId !== cartItem.tiles[0].userId
          );
          if (hasSoledTile) {
            cartItem.tiles.forEach((tile) => {
              if (!textureKeys.includes(cartItem.mainWallItemId + tile.id)) {
                const tilesDataForItem = [...tilesData[index]].sort(
                  (b: any, a: any) =>
                    Number(b.tileData.id) - Number(a.tileData.id)
                );

                this.drawCartItemImage(
                  cartItem,
                  tilesDataForItem,
                  hasSoledTile
                );
              }
            });
          } else {
            if (!textureKeys.includes(cartItem.mainWallItemId)) {
              const tilesDataForItem = [...tilesData[index]].sort(
                (b: any, a: any) =>
                  Number(b.tileData.id) - Number(a.tileData.id)
              );
              this.drawCartItemImage(cartItem, tilesDataForItem, hasSoledTile);
            }
          }
        }
      );
      this.changeWallDataLoading(false);
    });
  }

  private drawCartItemImage(
    cartItem: ComplexWallItem,
    tileData: any,
    hasSoledTile?: boolean
  ): void {
    if (!tileData) {
      return;
    }
    const size = hasSoledTile
      ? tileSize
      : tileSize * Math.sqrt(cartItem.tiles.length);
    const [mainTileData] = tileData;
    if (hasSoledTile) {
      tileData.forEach((td: any) => {
        this.drawImage(td, tileData, cartItem, size, hasSoledTile);
      });
    } else {
      this.drawImage(mainTileData, tileData, cartItem, size, hasSoledTile);
    }
  }

  private drawImage(
    tileData: any,
    tiles: any,
    cartItem: ComplexWallItem,
    tileSize: number,
    hasSoledTile?: boolean
  ): void {
    tiles.forEach((t: any) => t.tileSprite.setVisible(false));
    const { x, y } = tileData.tileSprite;
    const { id: tileId, image: imgSrc, creator } = tileData.tileData;
    const texture = hasSoledTile
      ? cartItem.mainWallItemId + tileId
      : cartItem.mainWallItemId;
    const img = new TileImage(this.scene, {
      x,
      y,
      texture,
      id: tileId,
      image: imgSrc,
      creator,
    });

    this.addAt(img, this.list.length - 1);
    img.setInteractive();
    img.setOrigin(0, 0);
    img.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (this.editedTileClicked) {
        return;
      }

      this.editedTileClicked = true;
      this.currentClickedImage = img;
      const clickedLeftSide = pointer.x <= this.scene.game.scale.width / 2;
      Events.emit({
        key: EventKey.ItemSelected,
        data: {
          cartItem,
          clickedLeftSide,
          tileId: hasSoledTile ? img.tileId : null,
        },
      });
    });
    img.displayWidth = tileSize;
    img.displayHeight = tileSize;
  }

  private subscribeToEvents(): void {
    Events.on(EventKey.CartData)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((event: EventsOption<unknown>) =>
        this.initializeCartData(event)
      );
    Events.on(EventKey.TileSizeClick)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((event) =>
        this.setCurrentSelectedTileSize(event as EventsOption<Point>)
      );
    Events.on(EventKey.ShowPopup)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        if (this.scene) {
          this.scene.input.enabled = false;
        }
      });
    Events.on(EventKey.HidePopup)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((event) => {
        if (event.data) {
          this.setCurrentSelectedTileSize();
        }
        this.checkAndRemoveSelectedTiles();
        this.editedTileClicked = false;
        this.scene.input.enabled = true;
      });
    Events.on(EventKey.RemovePreviewImage)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.destroyPreviewImage(true));
    Events.on(EventKey.TileDataUpdate)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((event) => this.updateTileData(event.data));
  }

  private destroyPreviewImage(setTileVisible: boolean = false): void {
    if (setTileVisible) {
      this.activeTile?.setVisible(true);
    }
    if (this.previewImage) {
      this.previewImage.destroy();
      this.scene.textures.remove(ImageKey.PreviewImage);
      this.scene.textures.removeKey(ImageKey.PreviewImage);
      this.previewImage = null;
    }
  }

  private initElevatorView() {
    this.elevatorView = new ElevatorView(this.scene);
    this.add(this.elevatorView);
    this.elevatorView.playElevatorDoorAnim(true);
  }

  private checkAndRemoveSelectedTiles(): void {
    this.activeTilesData?.forEach((atd) => {
      atd.tileSprite.setAlpha(0.0001);
      atd.tileSprite.setTint(0xffffff);
    });
  }

  private getTilesPositionFromTile(
    mainTilePosition: Position,
    size: Position
  ): Position[] {
    const tilePositions = [];
    for (let j = 0; j < size.y; j++) {
      for (let i = 0; i < size.x; i++) {
        tilePositions.push({
          x: mainTilePosition.x + i,
          y: mainTilePosition.y + j,
        });
      }
    }
    return tilePositions;
  }

  private setCurrentSelectedTileSize(event?: EventsOption<Point>): void {
    this.checkAndRemoveSelectedTiles();
    const size = !event ? this.defaultTileSize : (event.data as Point);
    const tileViewItem = this.tileViews.find(
      (t) => t.tileSprite === this.currentClickedTile
    );
    const activeTilePositions: Position[] = this.getTilesPositionFromTile(
      tileViewItem.tileData.position,
      size
    );

    this.activeTilesData = this.tileViews.filter((tv) =>
      activeTilePositions.some(
        (atp) =>
          atp.x === tv.tileData.position.x && atp.y === tv.tileData.position.y
      )
    );
    if (this.activeTilesData?.length > 0) {
      let tileColor: number = 0x75ff69;
      const data = {
        mainTileId: tileViewItem.tileData.id,
        tiles: this.getActiveTileIds((td) =>
          td.tileSprite.setAlpha(0.7).setTint(tileColor)
        ),
      };
      Events.emit({
        key: EventKey.TilesData,
        data,
      });
    }
  }

  private getActiveTileIds(cb?: (d: any) => void): string[] {
    return this.activeTilesData.map((td) => {
      cb?.(td);
      return td.tileData.id;
    });
  }

  private async loadTilesData(): Promise<void> {
    this.changeWallDataLoading(true);
    const zip = new JSZip();
    const content = await fetch(`${environment.s3Api}/tiles.json.zip`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/zip',
      },
    }).then((resp) => resp.blob());
    this.tilesData = await zip
      .loadAsync(content, { base64: true })
      .then((resp: JSZip) => (resp.files['tiles.json'] as any).async('string'))
      .then((data: string) => JSON.parse(data))
      .finally(() => this.changeWallDataLoading(false));
  }

  private calculateTilesBounds(): void {
    this.maximums = this.tilesData.reduce(
      (data: Position, item) => {
        if (item.position.x > data.x) {
          data.x = item.position.x;
        }
        if (item.position.y > data.y) {
          data.y = item.position.y;
        }
        return data;
      },
      { x: 0, y: 0 }
    );
  }

  private updateTileData(data?: any): void {
    // TODO: update tile image needs to be changed
    const frameKey = this.scene.textures.get(data.mainTileId)?.key;
    const { x, y, displayWidth, displayHeight } = this
      .currentClickedImage as Phaser.GameObjects.Image;
    this.currentClickedImage?.destroy();
    this.currentClickedImage = void 0;
    if (frameKey) {
      this.scene.textures.remove(frameKey);
      this.scene.textures.removeKey(frameKey);
    }
    this.scene.load.image(data.mainTileId, data.image);
    this.scene.load.start();
    this.scene.load.once(Phaser.Loader.Events.COMPLETE, () => {
      const image = this.scene.add.image(x, y, frameKey);
      image.setOrigin(0);
      image.displayWidth = displayWidth;
      image.displayHeight = displayHeight;
      image.setInteractive();
      this.add(image);
      image.on('pointerup', (pointer: Phaser.Input.Pointer) => {
        this.editedTileClicked = true;
        this.currentClickedImage = image;
        const clickedLeftSide = pointer.x <= this.scene.game.scale.width / 2;
        Events.emit({
          key: EventKey.ItemSelected,
          data: {
            cartItem: this.cartItems?.find(
              (ci: ComplexWallItem) => ci.mainWallItemId === data.mainTileId
            ),
            clickedLeftSide,
          },
        });
      });
    });
  }

  private getTilesData(prevCartItems?: ComplexWallItem[]): TileData[][] {
    return this.cartItems!.map((cartItem: ComplexWallItem) => {
      const hasSoledTile = cartItem.tiles.some(
        (tile) => tile.userId !== cartItem.tiles[0].userId
      );
      if (hasSoledTile) {
        cartItem.tiles.forEach((tile) => {
          this.scene.load.image(cartItem.mainWallItemId + tile.id, tile.image);
        });
      } else {
        if (
          this.hasImageChanged(cartItem, prevCartItems) &&
          this.scene.textures.getTextureKeys().includes(cartItem.mainWallItemId)
        ) {
          this.scene.textures.removeKey(cartItem.mainWallItemId);
        }
        this.scene.load.image(cartItem.mainWallItemId, cartItem.tiles[0].image);
      }
      return cartItem.tiles.map((cartItem: WallItem) =>
        this.tileViews.find((tv) => tv.tileData.id === cartItem.id)
      );
    });
  }

  private hasImageChanged(
    currItem: ComplexWallItem,
    prevCartItems?: ComplexWallItem[]
  ): boolean {
    return (
      !!prevCartItems &&
      prevCartItems.some(
        (p) =>
          p.mainWallItemId === currItem.mainWallItemId &&
          p.image !== currItem.image &&
          p.tiles[0].userId === currItem.tiles[0].userId
      )
    );
  }

  private changeWallDataLoading(loading: boolean): void {
    if (!this.scene) {
      return;
    }
    this.scene.input.enabled = !loading;
    Events.emit({
      key: EventKey.WallDataIsLoading,
      data: {
        loading,
      },
    });
  }
}
